import { useEffect, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import api from "../../../services/api";
import TicketModalContent from "../../tickets/detailsTicketComponent";
import TicketModalContentIxc from "../../tickets/detailsTicketIxcComponent";
import InternoModalConfirmacaoSaida from "../../modalSaindoDaPagina";
import { ERP_ATIVO } from "../config/formTicketParams";

// Abre o modal PADRÃO do sistema (detailsTicketComponent /
// detailsTicketIxcComponent — os mesmos das telas de suporte) para o
// atendimento/OS de um incidente da Central, a partir do id. Autocontido: o
// wrapper busca os detalhes e fornece o estado/callbacks que o modal espera
// (mesma receita do kanban de atendimentos, pages/internal/suporte/
// atendimentos — a única outra tela que instancia o modal fora da lista).
//
// Semântica por ERP (regra de produto):
// - HUBSOFT: a OS só existe DENTRO do atendimento — `tipo: "os"` abre o
//   modal do ticket (busca por atendimentoId) já na aba "Ordem de serviço"
//   (value 3 em detailsTicketComponent); exige o atendimento existir.
// - IXC: atendimento e OS são independentes — `tipo: "os"` busca a própria
//   OS (/interno/find/os/id, como a tela /interno/suporte/os) e abre o
//   modal IXC em modo OS.

// Índice da aba "Ordem de serviço" em detailsTicketComponent (HubSoft).
const ABA_OS_HUBSOFT = 3;

export const ERPS_COM_DETALHE_ATENDIMENTO = ["HUBSOFT", "IXC"];

const LABEL_TABS = { descricao: "Descrição", mensagens: "Mensagens" };

export default function ModalDetalheErp({
	aberto,
	tipo = "atendimento",
	atendimentoId = null,
	osId = null,
	onFechar,
}) {
	const theme = useTheme();
	const erp = ERP_ATIVO();
	const [carregando, setCarregando] = useState(true);
	const [ticketDetails, setTicketDetails] = useState(null);
	const [itensPublic, setItensPublic] = useState({});
	const [value, setValue] = useState(0);
	const [snackbar, setSnackbar] = useState(null);
	// Flags de edição do modal do sistema — bloqueiam o fechar sem confirmação.
	const [isEditTicket, setIsEditTicket] = useState(false);
	const [isFinishTicket, setIsFinishTicket] = useState(false);
	const [isEditOs, setIsEditOs] = useState(false);
	const [isNewOs, setIsNewOs] = useState(false);
	const [confirmacaoSaida, setConfirmacaoSaida] = useState(false);

	const abaInicial = erp === "HUBSOFT" && tipo === "os" ? ABA_OS_HUBSOFT : 0;

	useEffect(() => {
		if (!aberto) return undefined;
		let ativo = true;
		setValue(abaInicial);
		setTicketDetails(null);
		setIsEditTicket(false);
		setIsFinishTicket(false);
		setIsEditOs(false);
		setIsNewOs(false);
		setConfirmacaoSaida(false);

		(async () => {
			try {
				setCarregando(true);
				// HubSoft sempre busca o TICKET (a OS mora nele); IXC busca a
				// própria OS quando o marco clicado é de OS.
				const rota =
					erp === "IXC" && tipo === "os"
						? "/interno/find/os/id"
						: "/interno/find/tickets/id";
				const id = erp === "IXC" && tipo === "os" ? osId : atendimentoId;
				const { data } = await api.post(rota, { ticketId: id });
				if (!ativo) return;
				setTicketDetails(data || {});
				// Itens publicados para o cliente, agrupados por tipo — mesma
				// montagem do ticketComponent#handleOpen.
				const mensagens = [];
				const anexos = [];
				const anexosCliente = [];
				const rfo = [];
				for (const item of data?.itensTicketPublic || []) {
					if (item?.itemType === "mensagem") mensagens.push(item?.itemId);
					else if (item?.itemType === "anexo") anexos.push(item?.itemId);
					else if (item?.itemType === "anexoCliente")
						anexosCliente.push(item?.itemId);
					else if (item?.itemType === "rfo") rfo.push(item?.itemId);
				}
				setItensPublic({ mensagens, anexos, anexosCliente, rfo });
			} catch (error) {
				if (ativo) {
					setSnackbar({
						children:
							error?.response?.data?.message ||
							"Não foi possível carregar os detalhes no ERP.",
						severity: "error",
					});
					onFechar?.();
				}
			} finally {
				if (ativo) setCarregando(false);
			}
		})();

		return () => {
			ativo = false;
		};
	}, [aberto, tipo, atendimentoId, osId]); // eslint-disable-line react-hooks/exhaustive-deps

	const emEdicao = isEditTicket || isFinishTicket || isEditOs || isNewOs;
	const fechar = () => {
		setConfirmacaoSaida(false);
		onFechar?.();
	};
	const handleClose = () => {
		if (emEdicao) {
			setConfirmacaoSaida(true);
			return;
		}
		fechar();
	};

	// Cores das etapas do fluxo (HubSoft) — mesma montagem do kanban de
	// atendimentos (adicionarCoresModalDetails).
	const coresFluxo = () => {
		const nomes = Object.keys(theme.palette.colors || {}).filter(
			(cor) => cor !== "white" && cor !== "black",
		);
		return (ticketDetails?.allEtapasFluxoAtendimentos || []).reduce(
			(acc, etapa, indice) => {
				acc[etapa] = nomes[indice % nomes.length];
				return acc;
			},
			{},
		);
	};

	const propsComuns = {
		open: aberto,
		loading: carregando,
		handleClose,
		erp,
		ticketDetails: ticketDetails || {},
		status: ticketDetails?.status,
		labelTabsErp: LABEL_TABS,
		value,
		handleChange: (_, novaAba) => setValue(novaAba),
		isEdit: true,
		setTicketDetails,
		itensPublic,
		setItensPublic,
		setSnackbar,
		// Sem refetch aqui: os motores da Central têm cache de 90s — a tabela
		// reflete edições no próximo ciclo, como o resto da feature.
		setEditedTicket: () => {},
		setIsEditTicket,
		isEditTicket,
		isEditOs,
		setIsEditOs,
		setIsNewOs,
		isNewOs,
	};

	return (
		<>
			{aberto && erp === "HUBSOFT" && (
				<TicketModalContent
					{...propsComuns}
					fluxoAtendimentoColor={coresFluxo()}
					setIsFinishTicket={setIsFinishTicket}
					isFinishTicket={isFinishTicket}
				/>
			)}
			{aberto && erp === "IXC" && (
				<TicketModalContentIxc
					{...propsComuns}
					isAtendimento={tipo === "atendimento"}
					isOs={tipo === "os"}
					typeTitleModal={tipo === "os" ? "Ordem de serviço" : "Atendimento"}
				/>
			)}
			<InternoModalConfirmacaoSaida
				openConfirmacao={confirmacaoSaida}
				confirmLoad={false}
				setOpenConfirmacao={setConfirmacaoSaida}
				execute={fechar}
				cancel={() => setConfirmacaoSaida(false)}
				severity={"warning"}
				title={"Atenção"}
				contentText={"As alterações não salvas serão perdidas, deseja continuar?"}
				headerText={"Você está prestes a sair do modo de edição."}
				confirmText={"Continuar"}
			/>
			<Snackbar
				open={Boolean(snackbar)}
				autoHideDuration={3500}
				onClose={() => setSnackbar(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				{snackbar ? (
					<Alert
						onClose={() => setSnackbar(null)}
						severity={snackbar.severity}
						variant="filled"
					>
						{snackbar.children}
					</Alert>
				) : undefined}
			</Snackbar>
		</>
	);
}
